import { lexiconTrigrams } from './lexicon-trigram'
import {
  createDb,
  customIndex,
  index,
  sql,
  uniqueIndex,
  type DatabaseDriver,
} from '@w/db'
import type { Tel } from '@w/tel'
import { sqliteTable } from '@w/db/sqlite'
import { s } from '@w/schema'
import {
  assertValidEntry,
  cachedEntrySchema,
  entrySenses,
  sensesById,
  type CachedEntry,
  type CachedLexeme,
  type CachedRule,
} from './lexicon-model'
import type { LexiconCacheSearchPort, CacheSearchQuery } from './lexicon-cache-search'


export const cachedEntries = sqliteTable({
  columns: (c) => ({
    id: c.ulid().primaryKey(),
    language: c.text().notNull(),
    headword: c.text().notNull(),
    pos: c.text(),
    transcription: c.text().notNull(),
    // A lexeme has both, an MWE has neither. A row with pos set and
    // inflectionClass NULL escaped BOTH unique indexes — the full natural key
    // (NULLs never compare equal in a unique index) and the mwe partial index
    // (guarded on pos IS NULL) — so the dedupe guarantee was unenforced.
    inflectionClass: c
      .text()
      .constrain(
        'pos_pairing',
        (col) => sql`(${col} IS NULL) = (${sql.raw`"pos"`} IS NULL)`,
      ),
    entry: c.json(cachedEntrySchema).notNull(),
    adoptionCount: c.integer().notNull().default(0),
    createdAt: c.datetime().notNull(),
    updatedAt: c.datetime().notNull(),
    createdByUserId: c.ulid(),
  }),
  indexes: (t) => [
    uniqueIndex(
      t.language,
      t.headword,
      t.pos,
      t.transcription,
      t.inflectionClass,
    ),
    customIndex({
      name: 'idx_cachedEntries_mwe_natural_key',
      on: [t.language, t.headword, t.transcription],
      unique: true,
      where: sql.raw`${t.pos.col} IS NULL`,
    }),
    index(t.language, t.headword),
    index(t.language, t.headword, t.pos),
  ],
})

export const cachedRules = sqliteTable({
  columns: (c) => ({
    language: c.text().notNull(),
    key: c.text().notNull(),
    block: c.json(s.unknown()).notNull(),
    updatedAt: c.datetime().notNull(),
    title: c.text(),
  }),
  indexes: (t) => [uniqueIndex(t.language, t.key)],
})

export const lexiconCacheTables = {
  cachedEntries,
  cachedRules,
}

export function createLexiconCacheDb(driver?: DatabaseDriver, tel?: Tel, search?: LexiconCacheSearchPort, config: { bufferedTransactions?: boolean } = {}) {
  const db = createDb(lexiconCacheTables, {
    driver,
    fn: (db) => ({
      async searchEntries(query: CacheSearchQuery): Promise<CachedEntry[]> {
        if (lexiconTrigrams(query.text).length === 0 || (query.limit ?? 20) <= 0) return []
        if (!search) throw new Error('Cache search port must be injected')
        await _backfillSearch(db, search)
        return search.search(db.query, query)
      },
      upsertEntry(
        incoming: CachedEntry,
        options: { createdByUserId?: string } = {},
      ): Promise<{ id: string }> {
        return Promise.resolve().then(() => {
        assertValidEntry(incoming)
        const now = new Date()
        // D1 buffers mutations into one atomic batch and cannot read inside it.
        // Interactive drivers keep the natural-key lookup in the write transaction.
        if (config.bufferedTransactions) return _upsertBufferedEntry(db, search, incoming, options, now)
        return db.transaction(async (tx) => {
          const existing = await _findExistingEntry(tx, incoming)
          const id = existing?.id ?? incoming.id
          const entry = existing
            ? _mergeEntries(existing.entry, incoming, id)
            : incoming
          if (existing) {
            await tx.query`
              ${tx.cachedEntries.update({ ..._identityColumns(entry), entry, updatedAt: now })}
              WHERE ${_idEq(tx, id)}
            `.run()
          } else {
            await tx.query`${tx.cachedEntries.insert({
              ..._identityColumns(entry),
              id,
              entry,
              adoptionCount: 0,
              createdAt: now,
              updatedAt: now,
              createdByUserId: options.createdByUserId ?? null,
            })}`.run()
          }
          await search?.write(tx.query, tx.cachedEntries.id.encode(id), entry)
          return { id }
        })
        })
      },

      async getEntry(id: string): Promise<CachedEntry | undefined> {
        const row = await db.query`
          SELECT ${db.cachedEntries.all} FROM ${db.cachedEntries}
          WHERE ${db.cachedEntries.id.eq(id)} LIMIT 1
        `.executeOne(db.cachedEntries.__selectSchema)
        return row?.entry
      },

      async listByHeadword(
        language: string,
        headword: string,
      ): Promise<CachedEntry[]> {
        const rows = await db.query`
          SELECT ${db.cachedEntries.all} FROM ${db.cachedEntries}
          WHERE ${db.cachedEntries.language.eq(language)}
            AND ${db.cachedEntries.headword.eq(headword)}
          ORDER BY ${db.cachedEntries.id}
        `.execute(db.cachedEntries.__selectSchema)
        return rows.map((row) => row.entry)
      },

      async bumpAdoptionCount(id: string): Promise<void> {
        await db.query`
          UPDATE "cachedEntries"
          SET adoptionCount = adoptionCount + 1
          WHERE ${_idEq(db, id)}
        `.run()
      },

      async decrementAdoptionCount(id: string): Promise<void> {
        await db.query`
          UPDATE "cachedEntries"
          SET adoptionCount = MAX(adoptionCount - 1, 0)
          WHERE ${_idEq(db, id)}
        `.run()
      },

      deleteEntry(
        id: string,
        options: { requesterId: string },
      ): Promise<{ deleted: boolean }> {
        return Promise.resolve().then(async () => {
          const row = await db.query`
            SELECT ${db.cachedEntries.createdByUserId}
            FROM ${db.cachedEntries}
            WHERE ${_idEq(db, id)} LIMIT 1
          `.executeOne({ createdByUserId: db.cachedEntries.createdByUserId })
          if (row?.createdByUserId !== options.requesterId)
            return { deleted: false }

          return db.transaction(async (tx) => {
            await search?.write(tx.query, tx.cachedEntries.id.encode(id))
            await tx.query`
              DELETE FROM ${tx.cachedEntries}
              WHERE ${_idEq(tx, id)}
            `.run()
            return { deleted: true }
          })
        })
      },

      removeLocalEntry(id: string): Promise<void> {
        return Promise.resolve(
          db.transaction(async (tx) => {
            await search?.write(tx.query, tx.cachedEntries.id.encode(id))
            await tx.query`DELETE FROM ${tx.cachedEntries} WHERE ${_idEq(tx, id)}`.run()
          }),
        )
      },

      async upsertRule(rule: CachedRule): Promise<void> {
        await db.transaction(async (tx) => {
          await tx.query`
            DELETE FROM ${tx.cachedRules}
            WHERE ${tx.cachedRules.language.eq(rule.language)}
              AND ${tx.cachedRules.key.eq(rule.key)}
          `.run()
          await tx.query`${tx.cachedRules.insert({
            language: rule.language,
            key: rule.key,
            block: rule.block,
            title: rule.title ?? null,
            updatedAt: rule.updatedAt ?? new Date(),
          })}`.run()
        })
      },

      async getRule(
        language: string,
        key: string,
      ): Promise<CachedRule | undefined> {
        const row = await db.query`
          SELECT ${db.cachedRules.all} FROM ${db.cachedRules}
          WHERE ${db.cachedRules.language.eq(language)}
            AND ${db.cachedRules.key.eq(key)} LIMIT 1
        `.executeOne(db.cachedRules.__selectSchema)
        return (
          row && {
            language: language as CachedRule['language'],
            key,
            block: row.block as CachedRule['block'],
            ...(row.title === null ? {} : { title: row.title }),
            updatedAt: row.updatedAt,
          }
        )
      },

      // D1: inflection-class membership is never stored on the rule block. It is
      // derived by
      // reading the entries table at query time, not by maintaining a members array.
      async listByInflectionClass(
        language: string,
        inflectionClass: string,
      ): Promise<CachedLexeme[]> {
        const rows = await db.query`
          SELECT ${db.cachedEntries.all} FROM ${db.cachedEntries}
          WHERE ${db.cachedEntries.language.eq(language)}
            AND ${db.cachedEntries.inflectionClass.eq(inflectionClass)}
          ORDER BY ${db.cachedEntries.id}
        `.execute(db.cachedEntries.__selectSchema)
        return rows
          .map((row) => row.entry)
          .filter((entry): entry is CachedLexeme => entry.kind === 'lexeme')
      },
    }),
  })
  return tel ? db.withTel(tel) : db
}

export type LexiconCacheDb = ReturnType<typeof createLexiconCacheDb>

type _Db = LexiconCacheDb

async function _upsertBufferedEntry(
  db: _Db,
  search: LexiconCacheSearchPort | undefined,
  incoming: CachedEntry,
  options: { createdByUserId?: string },
  now: Date,
): Promise<{ id: string }> {
  for (let attempt = 0; ; attempt++) {
    const existing = await _findExistingEntry(db, incoming)
    const id = existing?.id ?? incoming.id
    const entry = existing ? _mergeEntries(existing.entry, incoming, id) : incoming
    const expected = existing ? db.cachedEntries.entry.encode(existing.entry) : ''
    try {
      const result = await db.transaction(async tx => {
        await tx.query`${tx.cachedEntries.insert({
          ..._identityColumns(entry), id, entry,
          adoptionCount: existing?.adoptionCount ?? 0,
          createdAt: existing?.createdAt ?? now, updatedAt: now,
          createdByUserId: existing?.createdByUserId ?? options.createdByUserId ?? null,
        })}
          ON CONFLICT(id) DO UPDATE SET
            entry = CASE WHEN cachedEntries.entry = ${expected} THEN excluded.entry ELSE NULL END,
            language = excluded.language, headword = excluded.headword,
            pos = excluded.pos, transcription = excluded.transcription,
            inflectionClass = excluded.inflectionClass,
            updatedAt = excluded.updatedAt`.run()
        await search?.write(tx.query, tx.cachedEntries.id.encode(id), entry)
        return { id }
      })
      return result
    } catch (error) {
      // A stale pre-read aborts the entire batch via entry's NOT NULL constraint.
      // A concurrently inserted natural key aborts via the existing unique index.
      // All other failures propagate, including failed posting writes.
      const conflict = error instanceof Error && /(?:NOT NULL constraint failed: cachedEntries\.entry|UNIQUE constraint failed: cachedEntries\.)/u.test(error.message)
      if (!conflict || attempt >= 2) throw error
    }
  }
}

async function _backfillSearch(db: _Db, search: LexiconCacheSearchPort): Promise<void> {
  // Backfill uses the same Unicode extractor as writes, never SQLite lower().
  // The source comparison in write(..., true) prevents stale reads from replacing
  // postings belonging to a newer generation, or resurrecting a deleted entry.
  const rows = await db.query`SELECT ${db.cachedEntries.id}, ${db.cachedEntries.entry}
    FROM ${db.cachedEntries}
    WHERE NOT EXISTS (SELECT 1 FROM lexTrgmDoc WHERE lexTrgmDoc.entryId = cachedEntries.id)`
    .execute({ id: db.cachedEntries.id, entry: db.cachedEntries.entry })
  for (const row of rows) {
    await db.transaction(tx => search.write(tx.query, tx.cachedEntries.id.encode(row.id), row.entry, true))
  }
}

function _idEq(db: _Db, id: string) {
  return db.cachedEntries.id.valueCondition(
    '"id"',
    '=',
    db.cachedEntries.id.encode(id),
  )
}

async function _findExistingEntry(db: _Db, entry: CachedEntry) {
  const existing = await db.query`SELECT ${db.cachedEntries.all} FROM ${db.cachedEntries}
    WHERE ${db.cachedEntries.id.eq(entry.id)} LIMIT 1`.executeOne(db.cachedEntries.__selectSchema)
  return existing ?? _findByNaturalKey(db, entry)
}

function _findByNaturalKey(db: _Db, entry: CachedEntry) {
  const identity = _identityColumns(entry)
  if (entry.kind === 'mwe') {
    return db.query`
      SELECT ${db.cachedEntries.all} FROM ${db.cachedEntries}
      WHERE ${db.cachedEntries.language.eq(identity.language)}
        AND ${db.cachedEntries.headword.eq(identity.headword)}
        AND ${db.cachedEntries.transcription.eq(identity.transcription)}
        AND ${db.cachedEntries.pos} IS NULL LIMIT 1
    `.executeOne(db.cachedEntries.__selectSchema)
  }
  return db.query`
    SELECT ${db.cachedEntries.all} FROM ${db.cachedEntries}
    WHERE ${db.cachedEntries.language.eq(entry.language)}
      AND ${db.cachedEntries.headword.eq(entry.headword)}
      AND ${db.cachedEntries.pos.eq(entry.pos)}
      AND ${db.cachedEntries.transcription.eq(entry.transcription)}
      AND ${db.cachedEntries.inflectionClass.eq(entry.inflectionClass)} LIMIT 1
  `.executeOne(db.cachedEntries.__selectSchema)
}

function _identityColumns(entry: CachedEntry) {
  return {
    language: entry.language,
    headword: entry.headword,
    pos: entry.kind === 'lexeme' ? entry.pos : null,
    transcription: entry.transcription,
    inflectionClass: entry.kind === 'lexeme' ? entry.inflectionClass : null,
  }
}

function _mergeEntries(
  existing: CachedEntry,
  incoming: CachedEntry,
  id: string,
): CachedEntry {
  const incomingByGloss = new Map(
    entrySenses(incoming).map((sense) => [sense.gloss, sense]),
  )
  const existingSenses = entrySenses(existing)
  const incomingSenses = entrySenses(incoming)
  const existingGlosses = new Set(existingSenses.map((sense) => sense.gloss))
  const senses = existingSenses.map((sense) => {
    const replacement = incomingByGloss.get(sense.gloss)
    return replacement
      ? Object.assign({}, replacement, { id: sense.id })
      : sense
  })
  senses.push(
    ...incomingSenses.filter((sense) => !existingGlosses.has(sense.gloss)),
  )
  return {
    ...incoming,
    id,
    translations: { ...existing.translations, ...incoming.translations },
    senses: sensesById(senses),
  }
}
